import { useState, useEffect } from "react";
import { getTodayDateString } from "@/lib/dateUtils";
import { queryClient } from "@/lib/queryClient";

/**
 * Returns today's date as a YYYY-MM-DD string, tracked in React state.
 *
 * Registers a visibilitychange listener so that when the user returns to the
 * tab after midnight the date flips automatically: the new value propagates to
 * any query key that includes it, triggering a fresh fetch without a manual
 * page reload.  All cached queries are invalidated on rollover so that
 * date-keyed data across the whole app stays consistent.
 */
export function useTodayDate(): string {
  const [today, setToday] = useState(() => getTodayDateString());

  useEffect(() => {
    function handleVisibilityChange() {
      if (document.visibilityState === "visible") {
        const freshDate = getTodayDateString();
        setToday((prev) => {
          if (prev !== freshDate) {
            queryClient.invalidateQueries();
            return freshDate;
          }
          return prev;
        });
      }
    }

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);

  return today;
}
